import React, { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MailCheck, ArrowLeft } from "lucide-react";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devUrl, setDevUrl] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (data.dev_reset_url) setDevUrl(data.dev_reset_url);
      toast.success("Check your email", { description: "If an account exists, we've sent a reset link." });
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Too many attempts", { description: err.response?.data?.detail || "Please wait a few minutes and try again." });
      } else {
        toast.error("Something went wrong", { description: err.response?.data?.detail || "Please try again." });
      }
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { document.title = "Forgot Password — Dream Home Plans"; }, []);

  return (
    <Layout>
      <div className="max-w-md mx-auto px-6 py-24">
        <div className="overline text-accent">Password reset</div>
        <h1 className="font-display text-5xl mt-3 mb-8 leading-none tracking-tighter">Forgot password?</h1>

        {!sent ? (
          <>
            <p className="text-muted-foreground mb-8">Enter your email and we'll send you a link to set a new password. The link expires in 60 minutes.</p>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label className="overline text-muted-foreground">Email</Label>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-none mt-2 h-12"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit-btn"
                className="w-full rounded-none h-12 bg-primary"
              >
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          </>
        ) : (
          <div data-testid="forgot-success">
            <MailCheck size={40} className="text-accent" />
            <div className="overline text-accent mt-6">Check your email</div>
            <h2 className="font-display text-3xl mt-3">Reset link sent</h2>
            <p className="mt-4 text-muted-foreground">
              If an account exists for <strong>{email}</strong>, you'll receive an email with a password reset link within a minute.
            </p>
            {devUrl && (
              <div className="mt-6 border border-accent bg-accent/10 p-4 text-sm" data-testid="dev-reset-url">
                <div className="overline text-accent mb-2">Dev mode (no Resend key)</div>
                <div className="break-all text-xs">
                  <a href={devUrl} className="underline text-accent">{devUrl}</a>
                </div>
              </div>
            )}
          </div>
        )}

        <Link to="/login" data-testid="back-to-login" className="mt-10 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent">
          <ArrowLeft size={14} /> Back to sign in
        </Link>
      </div>
    </Layout>
  );
};

export default ForgotPassword;
